import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import apiService from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { AuthScreenShell, AuthCard, AuthCardHeader } from "../components/auth";

const AcceptCompanyInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const { establishSessionFromAuthResponse } = useAuth();

  const [preview, setPreview] = useState<{
    kind: "email" | "link";
    companyName: string;
    email: string | null;
    emailMasked: string | null;
    companyRole: string;
    hasAccount: boolean;
    alreadyInCompany: boolean;
    inOtherCompany: boolean;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setPreviewError("Enlace inválido: falta el token de invitación.");
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await apiService.getCompanyInvitationPreview(token);
        if (!cancelled) {
          setPreview({
            kind: data.kind || "email",
            companyName: data.companyName,
            email: data.email || null,
            emailMasked: data.emailMasked,
            companyRole: data.companyRole,
            hasAccount: data.hasAccount,
            alreadyInCompany: data.alreadyInCompany,
            inOtherCompany: data.inOtherCompany,
          });
          if (data.email) {
            setEmail(data.email);
          }
        }
      } catch (e: any) {
        if (!cancelled) {
          setPreviewError(
            e.response?.data?.message ||
              "No pudimos cargar la invitación. Puede haber caducado o ya haber sido usada.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    if (!token) return;

    const isOpenLink = preview?.kind === "link";

    // Email-bound invite: existing account can continue without password form
    if (
      !isOpenLink &&
      preview?.hasAccount &&
      !preview.inOtherCompany
    ) {
      setSubmitting(true);
      try {
        const res = await apiService.acceptCompanyInvitation({ token });
        await establishSessionFromAuthResponse(res);
      } catch (err: any) {
        setSubmitError(
          err.response?.data?.message ||
            "No se pudo completar. Intenta iniciar sesión con tu email.",
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (isOpenLink) {
      const em = email.trim().toLowerCase();
      if (!em) {
        setSubmitError("Ingresa tu email");
        return;
      }
    }

    const n = name.trim();
    if (!n) {
      setSubmitError("Ingresa tu nombre");
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError("Las contraseñas no coinciden");
      return;
    }
    if (password.length < 6) {
      setSubmitError("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiService.acceptCompanyInvitation({
        token,
        name: n,
        password,
        ...(isOpenLink ? { email: email.trim().toLowerCase() } : {}),
      });
      await establishSessionFromAuthResponse(res);
    } catch (err: any) {
      setSubmitError(
        err.response?.data?.message ||
          "No se pudo completar el registro. Intenta de nuevo.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AuthScreenShell>
        <CircularProgress />
      </AuthScreenShell>
    );
  }

  if (previewError || !preview) {
    return (
      <AuthScreenShell>
        <AuthCard>
          <AuthCardHeader title="Invitación" />
          <Alert severity="error">{previewError || "Invitación no disponible"}</Alert>
        </AuthCard>
      </AuthScreenShell>
    );
  }

  if (preview.kind !== "link" && preview.inOtherCompany) {
    return (
      <AuthScreenShell>
        <AuthCard>
          <AuthCardHeader title="Invitación" />
          <Alert severity="warning">
            Este email ya está asociado a otra empresa. Inicia sesión con esa cuenta o
            contacta al administrador.
          </Alert>
        </AuthCard>
      </AuthScreenShell>
    );
  }

  if (
    preview.kind !== "link" &&
    preview.hasAccount &&
    !preview.inOtherCompany
  ) {
    return (
      <AuthScreenShell>
        <AuthCard>
          <AuthCardHeader
            title={preview.companyName}
            subtitle={
              preview.alreadyInCompany
                ? `Ya tienes una cuenta en esta empresa (${preview.emailMasked}). Pulsa continuar para iniciar sesión.`
                : `Tu cuenta (${preview.emailMasked}) se unirá a esta empresa. Pulsa continuar.`
            }
          />
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <Button
            variant="contained"
            fullWidth
            size="large"
            onClick={(e) => handleSubmit(e as unknown as React.FormEvent)}
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : "Continuar"}
          </Button>
        </AuthCard>
      </AuthScreenShell>
    );
  }

  const isOpenLink = preview.kind === "link";

  return (
    <AuthScreenShell>
      <AuthCard>
        <AuthCardHeader title={`Únete a ${preview.companyName}`} />

        <form onSubmit={handleSubmit}>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            required
            autoComplete="email"
            disabled={!!preview.email}
          />
          <TextField
            fullWidth
            label="Nombre completo"
            value={name}
            onChange={(e) => setName(e.target.value)}
            margin="normal"
            required
            autoComplete="name"
          />
          <TextField
            fullWidth
            label="Contraseña"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            autoComplete={isOpenLink ? "current-password" : "new-password"}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label="toggle password"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            fullWidth
            label="Confirmar contraseña"
            type={showPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            autoComplete="new-password"
          />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            sx={{ mt: 3 }}
            disabled={submitting}
          >
            {submitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : isOpenLink ? (
              "Continuar"
            ) : (
              "Crear cuenta"
            )}
          </Button>
        </form>
      </AuthCard>
    </AuthScreenShell>
  );
};

export default AcceptCompanyInvitation;
